import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, Users } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { usePolling } from "@/hooks/usePolling";
import { LastUpdated } from "@/components/common/LastUpdated";
import { ROLE_LABELS } from "@/lib/roles";
import { TeamsListPanel } from "@/components/org/teamAccess/TeamsListPanel";
import { MembersPanel } from "@/components/org/teamAccess/MembersPanel";
import { MemberDetailPanel } from "@/components/org/teamAccess/MemberDetailPanel";
import { TeamFormModal } from "@/components/org/teamAccess/TeamFormModal";
import { InviteMemberModal } from "@/components/org/teamAccess/InviteMemberModal";

export default function OrgTeamAccess() {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [savingUserId, setSavingUserId] = useState(null);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetMessage, setResetMessage] = useState("");
  const [resetMessageUserId, setResetMessageUserId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState("all");
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [teamModal, setTeamModal] = useState(null); // null | "new" | team object
  const [teamsCollapsed, setTeamsCollapsed] = useState(() => typeof window !== "undefined" && window.innerWidth < 1366);
  const [detailsCollapsed, setDetailsCollapsed] = useState(() => typeof window !== "undefined" && window.innerWidth < 1366);

  const load = useCallback(async (silent) => {
    if (!silent) setError("");
    try {
      const { data: resp } = await axios.get(`${API}/org/team`, { withCredentials: true });
      setData(resp);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  // Skip the silent auto-refresh while a modal is open or an action is in flight,
  // so an in-progress change is never overwritten mid-edit.
  usePolling(() => {
    if (showInviteModal || teamModal || savingUserId || resetUserId) return;
    load(true);
  }, 10000);

  const setSolutionAccess = async (userId, solutionId, level) => {
    setSavingUserId(userId);
    try {
      await axios.patch(`${API}/org/team/${userId}/solution-access`, { access: { [solutionId]: level } }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUserId(null);
    }
  };

  const changeRole = async (userId, role) => {
    setSavingUserId(userId);
    try {
      await axios.patch(`${API}/org/team/${userId}/role`, { role }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUserId(null);
    }
  };

  const toggleBilling = async (userId, field, current) => {
    setSavingUserId(userId);
    try {
      await axios.patch(`${API}/org/team/${userId}/billing-access`, { [field]: !current }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUserId(null);
    }
  };

  const toggleCatalogAccess = async (userId, current) => {
    setSavingUserId(userId);
    try {
      await axios.patch(`${API}/org/team/${userId}/catalog-access`, { catalog_manage: !current }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUserId(null);
    }
  };

  const addMemberToTeam = async (userId, teamId) => {
    if (!teamId) return;
    setSavingUserId(userId);
    try {
      await axios.post(`${API}/org/teams/${teamId}/members`, { user_id: userId }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUserId(null);
    }
  };

  const removeMemberFromTeam = async (userId, teamId) => {
    setSavingUserId(userId);
    try {
      await axios.delete(`${API}/org/teams/${teamId}/members/${userId}`, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUserId(null);
    }
  };

  const deactivateMember = async (userId) => {
    setSavingUserId(userId);
    setError("");
    try {
      await axios.post(`${API}/org/team/${userId}/deactivate`, {}, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUserId(null);
    }
  };

  const reactivateMember = async (userId) => {
    setSavingUserId(userId);
    setError("");
    try {
      await axios.post(`${API}/org/team/${userId}/reactivate`, {}, { withCredentials: true });
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUserId(null);
    }
  };

  const sendPasswordReset = async (userId) => {
    setResetUserId(userId);
    setResetMessage("");
    setError("");
    try {
      const { data: resp } = await axios.post(`${API}/org/team/${userId}/send-password-reset`, {}, { withCredentials: true });
      setResetMessage(resp.message);
      setResetMessageUserId(userId);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResetUserId(null);
    }
  };

  const describeAuditEvent = (e) => {
    const target = e.target_name ? ` for ${e.target_name}` : "";
    if (e.event_type === "role_changed") return `changed role from ${ROLE_LABELS[e.old_value] || e.old_value} to ${ROLE_LABELS[e.new_value] || e.new_value}${target}`;
    if (e.event_type === "billing_changed") {
      const label = e.field === "nova_billing_access" ? "Nova Billing" : "Customer Billing";
      return `${e.new_value ? "granted" : "revoked"} ${label} access${target}`;
    }
    if (e.event_type === "team_member_added") return `added a team member${target}`;
    if (e.event_type === "team_member_removed") return `removed a team member${target}`;
    if (e.event_type === "team_lead_designation_changed") return `${e.new_value ? "designated" : "removed"} team lead${target}`;
    if (e.event_type === "catalog_access_changed") return `${e.new_value ? "granted" : "revoked"} Catalog Image access${target}`;
    if (e.event_type === "solution_access_changed") {
      const solutionName = (data?.solutions || []).find((s) => s.solution_id === e.field)?.name || e.field;
      const levelLabels = { none: "None", view: "View", manage: "Manage" };
      return `set ${solutionName} access to ${levelLabels[e.new_value] || e.new_value}${target}`;
    }
    if (e.event_type === "user_deactivated") return `deactivated a user${target}`;
    if (e.event_type === "user_reactivated") return `reactivated a user${target}`;
    return e.event_type;
  };

  const teams = data?.teams || [];
  const members = data?.members || [];
  const assignableRoles = data?.assignable_roles || [];
  const teamRequiredRoles = data?.team_required_roles || [];
  const limits = data?.limits || {};
  const usage = data?.usage || {};

  const filteredMembers = useMemo(() => {
    if (selectedTeamId === "all") return members;
    return members.filter((m) => m.team_ids.includes(selectedTeamId));
  }, [members, selectedTeamId]);

  const selectedTeam = selectedTeamId === "all" ? null : teams.find((t) => t.team_id === selectedTeamId) || null;

  const leadCount = useMemo(() => {
    if (selectedTeamId === "all") return filteredMembers.filter((m) => (m.teams || []).some((t) => t.is_lead)).length;
    return filteredMembers.filter((m) => (m.teams || []).some((t) => t.team_id === selectedTeamId && t.is_lead)).length;
  }, [filteredMembers, selectedTeamId]);

  const solutionCount = useMemo(
    () => new Set(filteredMembers.flatMap((m) => Object.entries(m.solution_access || {}).filter(([, level]) => level !== "none").map(([sid]) => sid))).size,
    [filteredMembers],
  );

  const eligibleToAdd = useMemo(() => {
    if (selectedTeamId === "all") return [];
    return members.filter((m) => !m.is_owner && !m.team_ids.includes(selectedTeamId));
  }, [members, selectedTeamId]);

  const pendingInvitations = useMemo(() => {
    const all = data?.pending_invitations || [];
    if (selectedTeamId === "all") return all;
    return all.filter((inv) => (inv.team_ids || []).includes(selectedTeamId));
  }, [data, selectedTeamId]);

  useEffect(() => {
    if (!filteredMembers.some((m) => m.user_id === selectedMemberId)) {
      setSelectedMemberId(filteredMembers[0]?.user_id || null);
    }
  }, [filteredMembers, selectedMemberId]);

  const selectedMember = filteredMembers.find((m) => m.user_id === selectedMemberId) || null;

  const memberActivity = useMemo(() => {
    if (!selectedMember) return [];
    return (data?.audit_log || [])
      .filter((e) => e.target_user_id === selectedMember.user_id)
      .map((e) => ({ event_id: e.event_id, actor_name: e.actor_name, description: describeAuditEvent(e), timestamp: e.timestamp }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, data]);

  if (error && !data) {
    return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-team-error">{error}</p>;
  }
  if (!data) {
    return <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="org-team-loading">Loading team…</div>;
  }

  if (data.scope === "team_lead") {
    const myTeams = data.teams || [];
    return (
      <div className="space-y-6" data-testid="org-team-access-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Team & Access</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage who's on your team and what they can access.</p>
          </div>
          <LastUpdated timestamp={lastUpdated} testId="org-team-last-updated" />
        </div>
        <Panel title="My Team" subtitle="Add or remove Team Member role users from the team(s) you lead.">
          {error && <p className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-team-lead-error">{error}</p>}
          {myTeams.length === 0 ? (
            <EmptyState icon={Users} title="You are not currently leading a team." message="Ask your organization owner or admin to designate you as a lead of a team." testId="org-team-lead-empty" />
          ) : (
            <div className="space-y-5" data-testid="org-team-lead-teams">
              {myTeams.map((t) => {
                const memberIds = t.members.map((m) => m.user_id);
                const eligible = (data.eligible_users || []).filter((u) => !memberIds.includes(u.user_id));
                return (
                  <div key={t.team_id} className="rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid={`org-team-lead-team-${t.team_id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-slate-950 dark:text-white">{t.name}</p>
                      {eligible.length > 0 && (
                        <select
                          value=""
                          disabled={!!savingUserId}
                          onChange={(e) => addMemberToTeam(e.target.value, t.team_id)}
                          data-testid={`org-team-lead-add-${t.team_id}`}
                          className="rounded-lg border border-dashed border-slate-300 px-2 py-1 text-xs dark:border-white/15 dark:bg-white/5 dark:text-white"
                        >
                          <option value="">+ Add member</option>
                          {eligible.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
                        </select>
                      )}
                    </div>
                    <div className="mt-3 space-y-2">
                      {t.members.length === 0 ? (
                        <p className="text-xs text-slate-400">No members yet.</p>
                      ) : t.members.map((m) => (
                        <div key={m.user_id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-white/5" data-testid={`org-team-lead-member-${t.team_id}-${m.user_id}`}>
                          <span className="text-slate-700 dark:text-slate-300">{m.name || m.email}{m.is_lead ? " (Lead)" : ""}</span>
                          {!m.is_lead && (
                            <button type="button" onClick={() => removeMemberFromTeam(m.user_id, t.team_id)} data-testid={`org-team-lead-remove-${t.team_id}-${m.user_id}`} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 border-t border-slate-100 pt-3 dark:border-white/10">
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Assigned Service Areas</p>
                      {(t.service_areas || []).length === 0 ? (
                        <p className="mt-1 text-xs text-slate-400" data-testid={`org-team-lead-service-areas-empty-${t.team_id}`}>No service areas assigned to this team yet.</p>
                      ) : (
                        <div className="mt-1 flex flex-wrap gap-2" data-testid={`org-team-lead-service-areas-${t.team_id}`}>
                          {t.service_areas.map((sa) => (
                            <span key={sa.service_area_id} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">{sa.name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="org-team-access-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Team & Access</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage who's on your team and what they can access.</p>
        </div>
        <LastUpdated timestamp={lastUpdated} testId="org-team-last-updated" />
      </div>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-team-error-banner">{error}</p>}

      <p className="text-xs font-semibold text-slate-400 dark:text-slate-500" data-testid="org-team-limits-usage">
        Org Admins {usage.org_admins ?? 0}/{limits.max_org_admins ?? "—"} · Teams {usage.teams ?? 0}/{limits.max_teams ?? "—"} · Nova Billing Users {usage.nova_billing_users ?? 0}/{limits.max_nova_billing_users ?? "—"} — set by Nova Opsis.
      </p>

      <div
        className="grid grid-cols-1 gap-5 lg:h-[calc(100vh-260px)] lg:min-h-[560px] lg:grid-cols-[var(--teams-w)_var(--members-w)_var(--details-w)]"
        style={{ "--teams-w": teamsCollapsed ? "64px" : "1fr", "--members-w": "5fr", "--details-w": detailsCollapsed ? "64px" : "4fr" }}
        data-testid="org-team-access-columns"
      >
        <TeamsListPanel
          teams={teams}
          selectedTeamId={selectedTeamId}
          onSelect={setSelectedTeamId}
          totalMembers={members.length}
          onNewTeam={() => setTeamModal("new")}
          collapsed={teamsCollapsed}
          onToggleCollapse={() => setTeamsCollapsed((c) => !c)}
        />
        <MembersPanel
          team={selectedTeam}
          isAll={selectedTeamId === "all"}
          members={filteredMembers}
          memberCount={filteredMembers.length}
          leadCount={leadCount}
          solutionCount={solutionCount}
          pendingInvitations={pendingInvitations}
          selectedMemberId={selectedMemberId}
          onSelectMember={(userId) => { setSelectedMemberId(userId); setDetailsCollapsed(false); }}
          onInviteNew={() => setShowInviteModal(true)}
          eligibleToAdd={eligibleToAdd}
          onAddExisting={(userId) => addMemberToTeam(userId, selectedTeamId)}
          savingUserId={savingUserId}
          onEditTeam={() => setTeamModal(selectedTeam)}
          onPromoteToLead={(userId) => changeRole(userId, "team_lead")}
        />
        <MemberDetailPanel
          member={selectedMember}
          teams={teams}
          solutions={data.solutions}
          assignableRoles={assignableRoles}
          savingUserId={savingUserId}
          resetUserId={resetUserId}
          resetMessage={resetMessage}
          resetMessageUserId={resetMessageUserId}
          activity={memberActivity}
          collapsed={detailsCollapsed}
          onToggleCollapse={() => setDetailsCollapsed((c) => !c)}
          onChangeRole={changeRole}
          onToggleBilling={toggleBilling}
          onToggleCatalogAccess={toggleCatalogAccess}
          onSetSolutionAccess={setSolutionAccess}
          onAddTeam={addMemberToTeam}
          onRemoveTeam={removeMemberFromTeam}
          onDeactivate={deactivateMember}
          onReactivate={reactivateMember}
          onSendReset={sendPasswordReset}
        />
      </div>

      {showInviteModal && (
        <InviteMemberModal
          onClose={() => setShowInviteModal(false)}
          onInvited={load}
          teams={teams}
          assignableRoles={assignableRoles}
          teamRequiredRoles={teamRequiredRoles}
          defaultTeamId={selectedTeamId === "all" ? null : selectedTeamId}
        />
      )}
      {teamModal && (
        <TeamFormModal
          team={teamModal === "new" ? null : teamModal}
          onClose={() => setTeamModal(null)}
          onSaved={(team) => { load(); setSelectedTeamId(team.team_id); }}
        />
      )}
    </div>
  );
}
